import {type DatesRangeValue} from '@mantine/dates'
import {useDebouncedValue} from '@mantine/hooks'
import {useNavigate, useSearch} from '@tanstack/react-router'
import {useEffect, useMemo, useRef, useState} from 'react'

import {
    buildProjectListSearch,
    DEFAULT_PROJECT_STATUS_FILTER,
    parseStatusSearchParam,
    projectListSearchEquals,
    type ProjectSortState,
    searchToDateRange,
    searchToSortState,
    validateProjectListSearch,
} from './projects-search'
import type {ProjectLifecycleStatus} from './schemas'

export function useProjectsListFilters(syncUrlSearch: boolean) {
    const navigate = useNavigate()
    const rawSearch = useSearch({strict: false})
    const urlSearch = useMemo(
        () => validateProjectListSearch(rawSearch as Record<string, unknown>),
        [rawSearch],
    )
    const lastWrittenSearchRef = useRef(urlSearch)
    const restoringFromUrlRef = useRef(false)

    const [nameFilter, setNameFilter] = useState(() => urlSearch.name ?? '')
    const [customerIdFilter, setCustomerIdFilter] = useState<string | null>(
        () => urlSearch.customer ?? null,
    )
    const [scheduleDateRange, setScheduleDateRange] = useState<DatesRangeValue>(() =>
        searchToDateRange(urlSearch),
    )
    const [statusFilter, setStatusFilter] = useState<ProjectLifecycleStatus[]>(() =>
        urlSearch.status ? parseStatusSearchParam(urlSearch.status) : DEFAULT_PROJECT_STATUS_FILTER,
    )
    const [sortState, setSortState] = useState<ProjectSortState>(() => searchToSortState(urlSearch))
    const [hasUnbilledFilter, setHasUnbilledFilter] = useState<boolean>(
        () => urlSearch.unbilled === 'true',
    )

    const [debouncedName] = useDebouncedValue(nameFilter, 300)

    const intendedSearch = useMemo(
        () =>
            buildProjectListSearch({
                name: nameFilter,
                customerId: customerIdFilter,
                scheduleDateRange,
                statusFilter,
                sortState,
                hasUnbilled: hasUnbilledFilter,
            }),
        [
            nameFilter,
            customerIdFilter,
            scheduleDateRange,
            statusFilter,
            sortState,
            hasUnbilledFilter,
        ],
    )

    useEffect(() => {
        if (projectListSearchEquals(urlSearch, lastWrittenSearchRef.current)) {
            return
        }

        lastWrittenSearchRef.current = urlSearch
        restoringFromUrlRef.current = true
        setNameFilter(urlSearch.name ?? '')
        setCustomerIdFilter(urlSearch.customer ?? null)
        setScheduleDateRange(searchToDateRange(urlSearch))
        setStatusFilter(parseStatusSearchParam(urlSearch.status))
        setSortState(searchToSortState(urlSearch))
        setHasUnbilledFilter(urlSearch.unbilled === 'true')
    }, [urlSearch])

    useEffect(() => {
        if (!syncUrlSearch) {
            return
        }

        if (restoringFromUrlRef.current) {
            restoringFromUrlRef.current = false
            return
        }

        if (projectListSearchEquals(intendedSearch, lastWrittenSearchRef.current)) {
            return
        }

        if (projectListSearchEquals(intendedSearch, urlSearch)) {
            lastWrittenSearchRef.current = intendedSearch
            return
        }

        lastWrittenSearchRef.current = intendedSearch
        void navigate({
            to: '/projects',
            search: intendedSearch,
            replace: true,
        })
    }, [syncUrlSearch, intendedSearch, urlSearch, navigate])

    return {
        nameFilter,
        setNameFilter,
        customerIdFilter,
        setCustomerIdFilter,
        scheduleDateRange,
        setScheduleDateRange,
        statusFilter,
        setStatusFilter,
        sortState,
        setSortState,
        hasUnbilledFilter,
        setHasUnbilledFilter,
        debouncedName,
        scheduleDateFromFilter: scheduleDateRange[0],
        scheduleDateToFilter: scheduleDateRange[1],
    }
}
